import { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  EstateWorkItem,
  EstateWorkItemStatus,
  EstateWorkItemSubStatus,
  useEstateWorkItem_UpdateEstateWorkItemMutation,
} from 'queries';

import Spinner from 'common/spinner';
import { EstateWorkItemStatusLabel } from 'utils/estate';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import { estateWorkItemStatusOptions } from './task-list';

const useStyles = makeStyles(() => ({
  stackedDataFirst: {
    display: 'flex',
    lineHeight: '1.43',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusAssigned: {
    color: 'blue',
  },
  statusCompleted: {
    color: 'green',
  },
}));

type EstateWorkItemTaskStatusCellProps = {
  estateWorkItem: EstateWorkItem;
  refetchEstateWorkItems: () => void;
};

const EstateWorkItemTaskStatusCell = ({
  estateWorkItem,
  refetchEstateWorkItems,
}: EstateWorkItemTaskStatusCellProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updateEstateWorkItem, { loading: updateEstateWorkItemExecuting }] =
    useEstateWorkItem_UpdateEstateWorkItemMutation({
      onCompleted: () => {
        refetchEstateWorkItems();
        handleClose();
        showToast({
          message: 'Status updated successfully!',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating estate work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (updateEstateWorkItemExecuting) {
    return <Spinner />;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onChange = async (status: EstateWorkItemStatus) => {
    const subStatus =
      status === EstateWorkItemStatus.Completed
        ? EstateWorkItemSubStatus.Completed
        : estateWorkItem.subStatus;

    updateEstateWorkItem({
      variables: {
        input: {
          id: estateWorkItem.id,
          status,
          subStatus,
        },
      },
    });
  };

  const statusLabel = EstateWorkItemStatusLabel[estateWorkItem.status] || estateWorkItem.status;

  return (
    <>
      <div className={classes.stackedDataFirst}>
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreVertIcon />
        </IconButton>
        <div
          className={clsx({
            [classes.statusAssigned]: estateWorkItem.status === EstateWorkItemStatus.AssignedReady,
            [classes.statusCompleted]: estateWorkItem.status === EstateWorkItemStatus.Completed,
          })}
        >
          {statusLabel}
        </div>
      </div>
      <Menu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {estateWorkItemStatusOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === estateWorkItem.status}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default EstateWorkItemTaskStatusCell;
